"use client";

import { ArrowRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Gallery() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const caseStudies = [{
    client: "County Infrastructure",
    sector: "Public Works",
    year: "2024",
    description: "A multi-phase bridge replacement project requiring complex grant compliance and inter-agency coordination. We managed the full lifecycle from initial planning to final inspection, ensuring all federal funding requirements were met while minimizing community disruption.",
    engagement: "24 weeks",
    pillars: "Compliance + Delivery",
    outcome: "Federal funding secured",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/bridge_infrastructure.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/bridge_infrastructure.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/bridge_infrastructure.png-wvc-sizes",
    alt: "Modern bridge construction site with engineering team"
  }, {
    client: "Regional Water Authority",
    sector: "Utilities",
    year: "2024",
    description: "A critical utility upgrade project involving aging infrastructure replacement. We implemented a new project management framework that streamlined communication between engineering teams and local stakeholders, resulting in faster project delivery and improved transparency.",
    engagement: "32 weeks",
    pillars: "Planning + Execution",
    outcome: "40% faster delivery",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/utility_management.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/utility_management.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1706817/img/utility_management.png-wvc-sizes",
    alt: "Utility planning documents and site survey equipment"
  }];
  return <section data-section-id="19551" id="project-gallery" className="bg-background text-foreground py-32 md:py-44 font-default">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        <div ref={headerRef} className="grid grid-cols-4 md:grid-cols-12 gap-4 md:gap-6 pb-20 md:pb-28">
          <div className={`col-span-4 md:col-span-6 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
            <div className="text-[0.6875rem] font-medium uppercase text-muted-foreground mb-8" style={{
            letterSpacing: "0.18em"
          }}>
              Project Portfolio
            </div>
            <h2 className="font-medium text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.2vw, 4rem)",
            letterSpacing: "-0.025em",
            lineHeight: 1.02
          }}>
              Infrastructure projects,{" "}
              <span className="italic font-medium !text-[rgba(9,9,9,1)]" style={{
              color: "var(--chart-2)",
              transitionDelay: "120ms",
              opacity: headerVisible ? 1 : 0,
              transition: "opacity 320ms ease-out"
            }}>
                delivered
              </span>
              .
            </h2>
          </div>
          <div className={`col-span-4 md:col-start-7 md:col-span-6 md:pt-3 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
          transitionDelay: "60ms"
        }}>
            <p className="text-muted-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>
              A selection of engagements where planning, grant coordination, and hands-on implementation came together to deliver results for public agencies.
            </p>
          </div>
        </div>

        <div className="border-t border-border">
          {caseStudies.map((cs, i) => <CaseStudyRow key={i} data-index={i} cs={cs} />)}
        </div>
      </div>
    </section>;
}
function CaseStudyRow({
  cs,
  ...rest
}: {
  cs: {
    client: string;
    sector: string;
    year: string;
    description: string;
    engagement: string;
    pillars: string;
    outcome: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className="group relative border-b border-border py-16 md:py-24 transition-all duration-[520ms] ease-[cubic-bezier(0.2,0.6,0.2,1)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(12px)"
  }}>
      <span aria-hidden="true" className="absolute -top-px left-0 right-0 h-px bg-[color:var(--chart-2)] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[220ms] ease-[cubic-bezier(0.2,0.6,0.2,1)]" />

      <div className="grid grid-cols-4 md:grid-cols-12 gap-4 md:gap-6 items-start">
        <div className="col-span-4 md:col-span-5">
          <div className="overflow-hidden rounded-lg">
            <AspectRatio ratio={4 / 5}>
              <img src={cs.image} data-wvc-srcset={cs.srcset} data-wvc-sizes={cs.sizes} alt={cs.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </AspectRatio>
            <div className="relative pt-4 mt-4 border-t border-border">
              <span aria-hidden="true" className="absolute -top-px left-0 h-px w-0 bg-[color:var(--chart-2)] group-hover:w-full transition-[width] duration-[600ms] ease-[cubic-bezier(0.2,0.6,0.2,1)]" />
              <div className="text-xs text-muted-foreground" style={{
              letterSpacing: "0.02em",
              lineHeight: 1.45
            }}>
                {cs.client} — {cs.sector}
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-4 md:col-start-7 md:col-span-3">
          
          <h3 className="font-medium text-foreground transition-transform duration-[220ms] ease-[cubic-bezier(0.2,0.6,0.2,1)] group-hover:translate-x-1" style={{
          fontSize: "clamp(1.5rem, 2.1vw, 2rem)",
          letterSpacing: "-0.015em",
          lineHeight: 1.15
        }}>
            {cs.client}
          </h3>
          <p className="text-foreground mt-6" style={{
          fontSize: "0.9375rem",
          letterSpacing: 0,
          lineHeight: 1.6
        }}>
            {cs.description}
          </p>
        </div>

        <div className="col-span-4 md:col-start-10 md:col-span-3">
          <dl className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-0">
            <MetaRow label="Engagement" value={cs.engagement} />
            <MetaRow label="Pillars" value={cs.pillars} />
            <MetaRow label="Outcome" value={cs.outcome} isLast />
          </dl>

          <div className="mt-8">
            <Link to="/ContactPage" className="inline-flex items-center gap-2 text-foreground group-hover:text-[color:var(--chart-2)] transition-colors duration-200" style={{
            fontSize: "0.9375rem",
            letterSpacing: 0
          }}><span className="border-b border-border group-hover:border-[color:var(--chart-2)] pb-1 transition-colors duration-200" style={{
              textUnderlineOffset: "4px"
            }}>
                Contact us for support
              </span>
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></Link>
          </div>
        </div>
      </div>
    </div>;
}
function MetaRow({
  label,
  value,
  isLast = false
}: {
  label: string;
  value: string;
  isLast?: boolean;
}) {
  return <div className={`grid grid-cols-[auto_1fr] gap-3 py-3 ${isLast ? "" : "border-b border-border"}`}>
      <dt className="text-xs text-muted-foreground uppercase" style={{
      letterSpacing: "0.02em",
      lineHeight: 1.45
    }}>
        {label}
      </dt>
      <dd className="text-foreground text-right" style={{
      fontSize: "0.9375rem",
      lineHeight: 1.45
    }}>
        {value}
      </dd>
    </div>;
}